/**
 * Resolving a URL to a place in the curriculum tree. `container` is anything
 * holding a slug-keyed `nodes` map - the curriculum, or any node in it.
 */

/** One level of the walk: the node at `slug`, its siblings, and what follows it. */
const getRouteInfo = (slug, nodes, parentNode) => {
  const plainNodes = Object.values(nodes || {});
  const current = plainNodes.find((node) => node.slug === slug);
  const currentIndex = plainNodes.indexOf(current);
  const isLast = currentIndex + 1 === plainNodes.length;
  const nextItem = isLast ? null : plainNodes[currentIndex + 1];

  return { plainNodes, current, parentNode, path: slug, isLast, nextItem };
};

/** The next sibling of the deepest ancestor that has one; does not descend into it. */
const getNextItem = (container, parts) => {
  if (!container || !parts) {
    return undefined;
  }

  let current = container;
  let levels = [];

  for (const part of parts) {
    const level = getRouteInfo(part, current.nodes, current);

    // A segment that does not resolve has no siblings of its own, and the old
    // code read its index as -1 and answered with the first sibling instead.
    if (!level.current) {
      return undefined;
    }

    levels.push(level);
    current = level.current;
  }

  levels = levels.reverse();

  const deepestWithASibling = levels.find((level) => !level.isLast);

  if (deepestWithASibling) {
    return deepestWithASibling.nextItem;
  }

  // Everything on the way down was the last of its siblings: the old code
  // returned the top-level node here, and pages rely on getting *something*.
  return levels[levels.length - 1].current;
};

/**
 * Resolves `path` against `container`. A path that does not resolve gives
 * `current: undefined` and a `chain` that stops at the last node that did.
 */
export const getCurrentNode = (container, path, prefix = '/gwc') => {
  let next = null;
  let previous = null;

  if (!container) {
    return { current: null, next, previous };
  }

  const parts = path
    .replace(prefix, '')
    .split('/')
    .filter((part) => part.length > 0);

  let current = container;
  let parentNode = null;

  const chain = [];

  for (const part of parts) {
    if (!current) {
      continue;
    }

    const siblings = Object.values(current.nodes || {});
    const index = siblings.findIndex((node) => node.slug === part);

    if (index !== -1) {
      // One node per level; the old loop pushed every sibling whose slug matched.
      chain.push(siblings[index]);
      previous = index > 0 ? siblings[index - 1] : null;
      next = getNextItem(container, parts);
    }

    parentNode = { ...current };
    current = current.nodes ? current.nodes[part] : undefined;
  }

  return {
    chain,
    current,
    next,
    parentNode,
    previous,
    parts,
  };
};

// A curriculum copied inside one Contentful environment renames clashing slugs
// to <slug>-copy-<id6>, while links in its content still name the original.
const COPY_SUFFIX = /-copy-[a-z0-9]{6}$/;

/**
 * The node a `path` written for another program's tree means in `container`:
 * content links are absolute, while their activities are shared. Matched by
 * the last slug; null when the tree has no such node.
 */
export const findNodeByPathTail = (container, path, prefix = '/gwc') => {
  const slug = path.replace(prefix, '').split('/').filter(Boolean).pop()?.replace(COPY_SUFFIX, '');

  if (!container || !slug) {
    return null;
  }

  for (const node of Object.values(container.nodes || {})) {
    if (node.slug?.replace(COPY_SUFFIX, '') === slug) {
      return node;
    }

    const found = findNodeByPathTail(node, path, prefix);

    if (found) {
      return found;
    }
  }

  return null;
};
